//#region Imports
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { styled, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import SearchResults, { Hit } from "_shared/_api/search/search-results";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { Dictionary } from "lodash";
import { marketplaceApi } from "marketplace/_api/marketplace-api";
import MarketplaceSearchRequestSchema, { MarketplaceSearchRequestJSON } from "marketplace/_schema/marketplace-search-request-schema";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import ResultSkeleton from "marketplace/_views/result-skeleton";
import ResultsList from "marketplace/_views/results-list";
import Individual from "marketplace/individuals/individual";
import MarketplaceTeamSearchSchema, { MarketplaceTeamSearchJSON } from "marketplace/teams/_api/_schemas/marketplace-team-search-schema";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { ChangeEvent, useCallback, useEffect } from "react";
import { NavigateFunction, SetURLSearchParams } from "react-router";
import Session from "users/__session/session";
//#endregion

const ComponentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    paddingRight: 0,
  },
  display: "flex",
}));
const ResultCount = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    marginLeft: theme.spacing(2),
  },
}));
const LowerPagination = styled(Grid)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: theme.spacing(1),
}));
//#region Styles
const UpperPagination = styled(Grid)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));
//#endregion

type ResultsProps = {
  session: Readonly<Session>;
  searchTerm: string;
  category: string;
  filters: Dictionary<string>;
  sortBy: string;
  embedded?: boolean;
  selectingTeamLeader?: boolean;
  selectingTeamMember?: boolean;
  selectingTeam?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  navigate?: NavigateFunction;
  searchParams?: URLSearchParams;
  setSearchParams?: SetURLSearchParams;
  onResults: (results: number | undefined) => void;
  onTeamMemberSelected?: (teamMember: Individual) => void;
  onTeamSelected?: (team: Team) => void;
//#endregion
};

function isConditionAbortError(error: unknown): boolean {
  const e = error as
    | { name?: string; message?: string; data?: unknown }
    | undefined;

  const message =
    e?.message ??
    (typeof e?.data === "string"
      ? e.data
      : (e?.data as { message?: string } | undefined)?.message);

  return (
    e?.name === "ConditionError" ||
    (typeof message === "string" &&
      message.includes("Aborted due to condition callback returning false"))
  );
}

  //#region Component
export default function Results(props: Readonly<ResultsProps>) {
  const {
    session,
    searchTerm,
    category,
    filters,
    sortBy,
    embedded,
    selectingTeamLeader,
    selectingTeamMember,
    selectingTeam,
    selectedTeamMemberUserIds,
    searchParams,
    setSearchParams,
    navigate,
    onResults,
    onTeamMemberSelected,
    onTeamSelected,
  //#endregion
  } = props;

  const dispatch = useAttorneyHubDispatch();
  const isMarketplaceSearchMode = !selectingTeam && !selectingTeamLeader && !selectingTeamMember;

  const [searchAllArgs, setSearchAllArgs] = React.useState<{ search: MarketplaceSearchRequestJSON } | undefined>();
  const [searchIndividualsArgs, setSearchIndividualsArgs] = React.useState<
    { search: MarketplaceSearchRequestJSON; limitToVendorReps?: boolean } | undefined
  >();
  const [searchTeamsArgs, setSearchTeamsArgs] = React.useState<{ search: MarketplaceTeamSearchJSON } | undefined>();
  const [individualByIdArgs, setIndividualByIdArgs] = React.useState<{ individualId: string } | undefined>();

  const searchAllQueryState = marketplaceApi.endpoints.searchAll.useQueryState(searchAllArgs ?? skipToken);
  const searchIndividualsQueryState = marketplaceApi.endpoints.searchIndividuals.useQueryState(
    searchIndividualsArgs ?? skipToken,
  );
  const searchTeamsQueryState = marketplaceApi.endpoints.searchTeams.useQueryState(searchTeamsArgs ?? skipToken);
  const individualByIdQueryState = marketplaceApi.endpoints.getMarketplaceIndividualById.useQueryState(
    individualByIdArgs ?? skipToken,
  );

  const marketplaceSearchLoading = searchAllQueryState.isLoading || searchAllQueryState.isFetching;
  const marketplaceSearchError = searchAllQueryState.error;
  const selectionModeLoading =
    searchIndividualsQueryState.isLoading ||
    searchIndividualsQueryState.isFetching ||
    searchTeamsQueryState.isLoading ||
    searchTeamsQueryState.isFetching ||
    individualByIdQueryState.isLoading ||
    individualByIdQueryState.isFetching;

  const [results, setResults] = React.useState<Hit<MarketplaceSearchInfoJSON>[]>([]);
  const [page, setPage] = React.useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
  const [numResults, setNumResults] = React.useState<number>(0);
  const loading = isMarketplaceSearchMode ? marketplaceSearchLoading : selectionModeLoading;
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"), {
    matchMedia: globalThis.matchMedia,
  });

  const hasInitializedQueryEffectRef = React.useRef(false);
  const lastQuerySignatureRef = React.useRef<string>("");

  const fetchResultList = useCallback(async (
    searchTerm: string,
    filters: Dictionary<string> | undefined,
    options?: { forceRefetch?: boolean },
  ) => {
    try {
      let response:
        | SearchResults<MarketplaceSearchInfoJSON>
        | undefined;
      const request = MarketplaceSearchRequestSchema.deserialize(filters);

      if (selectingTeam) {
        const teamSearch = MarketplaceTeamSearchSchema.deserialize({
          from: page && rowsPerPage ? (page - 1) * rowsPerPage : 0,
          size: rowsPerPage,
          query: searchTerm,
          contactPerson: filters?.contactPerson,
          description: filters?.description,
          name: filters?.name,
          team: filters?.team,
        });
        setSearchTeamsArgs({ search: teamSearch });
        const searchTeamsResult = await dispatch(
          marketplaceApi.endpoints.searchTeams.initiate(
            { search: teamSearch },
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );

        if ("error" in searchTeamsResult) {
          if (!isConditionAbortError(searchTeamsResult.error)) {
            throw searchTeamsResult.error;
          }
          return;
        }

        if ("data" in searchTeamsResult) {
          response = searchTeamsResult.data;
        }
      } else if (selectingTeamLeader) {
        setSearchIndividualsArgs({ search: request, limitToVendorReps: true });
        const searchIndividualsResult = await dispatch(
          marketplaceApi.endpoints.searchIndividuals.initiate(
            { search: request, limitToVendorReps: true },
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );

        if ("error" in searchIndividualsResult) {
          if (!isConditionAbortError(searchIndividualsResult.error)) {
            throw searchIndividualsResult.error;
          }
          return;
        }

        if ("data" in searchIndividualsResult) {
          response = searchIndividualsResult.data;
        }
      } else if (selectingTeamMember) {
        setSearchIndividualsArgs({ search: request });
        const searchIndividualsResult = await dispatch(
          marketplaceApi.endpoints.searchIndividuals.initiate(
            { search: request },
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );

        if ("error" in searchIndividualsResult) {
          if (!isConditionAbortError(searchIndividualsResult.error)) {
            throw searchIndividualsResult.error;
          }
          return;
        }

        if ("data" in searchIndividualsResult) {
          response = searchIndividualsResult.data;
        }
      } else {
        setSearchAllArgs({ search: request });
        const searchResult = await dispatch(
          marketplaceApi.endpoints.searchAll.initiate(
            { search: request },
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );

        if ("error" in searchResult) {
          if (!isConditionAbortError(searchResult.error)) {
            throw searchResult.error;
          }
        }

        if ("data" in searchResult && searchResult.data) {
          const hits = searchResult.data.hits ?? [];
          setResults(hits);
          setNumResults(hits.length ?? 0);
          setPage(searchResult.data.from ?? 0);
          setRowsPerPage(searchResult.data.size ?? 25);
          onResults(hits.length);
        }

        return;
      }

      if (!response) {
        return;
      }

      let hits = response.hits ? [...response.hits] : [];

      // Add self to the results list if the user is selecting a vendor-team leader
      if (
        selectingTeamLeader &&
        session.user?.individualId &&
        !response.hits?.some((hit) => hit.data.id === session.user?.individualId?.value)
      ) {
        setIndividualByIdArgs({ individualId: session.user.individualId.value });
        const selfResultResponse = await dispatch(
          marketplaceApi.endpoints.getMarketplaceIndividualById.initiate(
            { individualId: session.user.individualId.value },
            {
              subscribe: false,
              forceRefetch: options?.forceRefetch ?? false,
            },
          ),
        );

        if ("error" in selfResultResponse) {
          if (!isConditionAbortError(selfResultResponse.error)) {
            throw selfResultResponse.error;
          }
        }

        const self = "data" in selfResultResponse ? selfResultResponse.data : undefined;
        if (self) {
          const selfResult: Hit<MarketplaceSearchInfoJSON> = {
            data: self as unknown as MarketplaceSearchInfoJSON,
            score: 0,
            highlight: {}
          };
          hits = [selfResult, ...hits];
        }
      }

      setResults(hits);
      setNumResults(hits.length ?? 0);
      setPage(response.from ?? 0);
      setRowsPerPage(response.size ?? 25);

      onResults(hits.length);
    } catch (error) {
      if (!isConditionAbortError(error)) {
        console.error(error);
        enqueueSnackbar("Couldn't get results. Please try again", {
          variant: "error",
        });
      }
    }
  }, [dispatch, page, rowsPerPage, selectingTeam, selectingTeamLeader, selectingTeamMember, session, onResults]);

  useEffect(() => {
    if (
      !isMarketplaceSearchMode ||
      !marketplaceSearchError ||
      isConditionAbortError(marketplaceSearchError)
    ) {
      return;
    }

    enqueueSnackbar("Couldn't get results. Please try again", {
      variant: "error",
    });
  }, [isMarketplaceSearchMode, marketplaceSearchError]);

  const searchParamsStr = searchParams?.toString() ?? "";
  const filtersJson = JSON.stringify(filters ?? {});

  useEffect(() => {
    const querySignature = [
      searchTerm ?? "",
      category ?? "",
      sortBy ?? "",
      searchParamsStr,
      filtersJson,
    ].join("|");

    if (hasInitializedQueryEffectRef.current && lastQuerySignatureRef.current === querySignature) {
      return;
    }

    hasInitializedQueryEffectRef.current = true;
    lastQuerySignatureRef.current = querySignature;

    async function checkQueryParams() {
      const forceRefetch = Object.keys(filters ?? {}).length === 0;

      if (!searchParams?.has("query") && !searchParams?.has("id") && !searchParams?.has("type")) {
        await fetchResultList(searchTerm, filters, { forceRefetch });
        return;
      }

      const id = searchParams?.get("id") ?? "";
      const type = searchParams?.get("type") ?? "";

      if (id !== "" && type !== "") {
        navigate?.(`/marketplace/${type}/${id}`);
        return;
      }

      const term = searchParams?.get("query") ?? searchTerm ?? "";
      const queryFilters: Dictionary<string> = {
        ...(filters ?? {}),
      };

      if (term) {
        queryFilters.query = term;
      }

      if (category) {
        queryFilters.category = category;
      }

      if (sortBy) {
        queryFilters.sortBy = sortBy;
      }

      setPage(Number.parseInt(searchParams?.get("pageIndex") ?? "0"));
      await fetchResultList(term, queryFilters, { forceRefetch });
    }

    void checkQueryParams();
  }, [searchTerm, category, sortBy, searchParamsStr, filtersJson, fetchResultList, filters, navigate, searchParams]);

  async function handlePageChange(_event: ChangeEvent<unknown>, page: number) {
    setResults([]);
    setPage(page);

    if (searchParams && setSearchParams && isMarketplaceSearchMode) {
      const params = new URLSearchParams(searchParams); // clone, do not mutate prop object
      params.set("pageIndex", page.toString());
      setSearchParams(params);
      return;
    }
    
    await fetchResultList(searchTerm, filters);
  }

  return (
    <ComponentContainer>
      <Stack sx={{ flex: 1, width: "100%" }}>
        <Grid>
          {loading && <ResultSkeleton />}
          {!loading && (
            <UpperPagination container>
              <Grid>
                <ResultCount variant="h6">
                  {numResults} Result
                  {numResults < 1 || numResults > 1 ? "s" : ""}
                </ResultCount>
              </Grid>
              {numResults > rowsPerPage && (
                <Grid>
                  <Pagination
                    count={Math.ceil(numResults / rowsPerPage)}
                    page={page}
                    size={isMediumDisplaySize ? "medium" : "large"}
                    onChange={handlePageChange}
                  />
                </Grid>
              )}
            </UpperPagination>
          )}
        </Grid>
        {!loading && (
          <ResultsList
            results={results}
            embedded={embedded}
            selectingTeamLeader={selectingTeamLeader}
            selectingTeamMember={selectingTeamMember}
            selectingTeam={selectingTeam}
            selectedTeamMemberUserIds={selectedTeamMemberUserIds}
            navigate={navigate}
            onTeamMemberSelected={onTeamMemberSelected}
            onTeamSelected={onTeamSelected}
          />
        )}
        {numResults > rowsPerPage && !loading && (
          <LowerPagination size="grow">
            <Pagination
              count={Math.ceil(numResults / rowsPerPage)}
              page={page}
              disabled={Math.ceil(numResults / rowsPerPage) === 1}
              size={isMediumDisplaySize ? "medium" : "large"}
              onChange={handlePageChange}
            />
          </LowerPagination>
        )}
      </Stack>
    </ComponentContainer>
  );
}

//#endregion
